/**
 * Provider que gerencia notificações WebSocket de pedidos
 * 
 * Integra com o sistema de notificações existente e atualiza o estado
 * dos pedidos no OrderContext quando eventos chegam via WebSocket.
 * 
 * Deve ser montado no App.tsx junto com MessagesProvider
 */

import React, { useCallback, useRef } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';
import { useOrdersWebSocket } from '../../hooks/useOrdersWebSocket';
import { useOrders } from '../../contexts/OrderContext';
import { useInvalidateOrders } from '../../hooks/useOrdersQueries';
import { notify } from '../../lib/globalNotify';
import { playNotificationSound } from '../../lib/soundNotification';
import { sendToTerminal } from '../../lib/terminalLogger';
import type {
  OrderItemRefusedEvent,
  OrderPaymentStatusChangedEvent,
  OrderItemSavedEvent,
  OrderItemStageChangedEvent,
  OrderItemExportedEvent,
  OrderItemLayoutExportedEvent,
  OrderItemOpenedEvent,
} from '../../types/orderEvents';

const LOG_PREFIX = '[ORDERS_NOTIFICATION_HANDLER]';

/**
 * Provider que gerencia notificações WebSocket de pedidos
 */
export function OrdersNotificationHandler() {
  const { userInfo } = usePermissions();
  const { pedidos, fetchPedidos, selectPedido, updatePedidoInList } = useOrders();
  const invalidateOrders = useInvalidateOrders();

  // Ref para rastrear eventos processados (deduplicação)
  const processedEventsRef = useRef<Set<string>>(new Set());
  
  // Função auxiliar para gerar chave de deduplicação
  const getDedupKey = useCallback((eventType: string, pedidoId: number, itemId?: number, timestamp?: string) => {
    const timeWindow = timestamp ? Math.floor(new Date(timestamp).getTime() / (5 * 60 * 1000)) : Math.floor(Date.now() / (5 * 60 * 1000));
    return `${eventType}|${pedidoId}|${itemId ?? 'all'}|${timeWindow}`;
  }, []);

  // Função auxiliar para navegar para pedido
  const navigateToPedido = useCallback((pedidoId: number, itemId?: number) => {
    try {
      // Buscar pedido na lista atual
      const pedido = pedidos.find(p => p.orc_id === pedidoId);
      
      if (pedido) {
        // Selecionar pedido no contexto
        selectPedido(pedido);
        sendToTerminal(`✅ ${LOG_PREFIX} Pedido selecionado para navegação`, {
          pedidoId,
          itemId,
        });
        
        // Emitir evento customizado para navegar para página de pedidos
        // O App.tsx escutará este evento e navegará para a página
        window.dispatchEvent(new CustomEvent('navigate:to-pedido', {
          detail: {
            pedidoId,
            itemId,
          },
        }));
      } else {
        // Pedido não está na lista atual, fazer refresh
        sendToTerminal(`🔄 ${LOG_PREFIX} Pedido não encontrado, fazendo refresh`, {
          pedidoId,
        });
        fetchPedidos(true).then(() => {
          // Tentar novamente após refresh
          const pedidoAtualizado = pedidos.find(p => p.orc_id === pedidoId);
          if (pedidoAtualizado) {
            selectPedido(pedidoAtualizado);
            // Emitir evento de navegação
            window.dispatchEvent(new CustomEvent('navigate:to-pedido', {
              detail: {
                pedidoId,
                itemId,
              },
            }));
          }
        });
      }
    } catch (error) {
      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao navegar para pedido`, {
        error: error instanceof Error ? error.message : String(error),
        pedidoId,
      });
    }
  }, [pedidos, selectPedido, fetchPedidos]);

  // Handler para item recusado
  const handleItemRefused = useCallback((event: OrderItemRefusedEvent) => {
    const dedupKey = getDedupKey('refused', event.pedidoId, event.itemId, event.recusadoEm);
    
    // Deduplicação
    if (processedEventsRef.current.has(dedupKey)) {
      sendToTerminal(`⏭️ ${LOG_PREFIX} Evento recusado duplicado, ignorando`, { dedupKey });
      return;
    }
    processedEventsRef.current.add(dedupKey);
    
    // Limpar chaves antigas (mais de 10 minutos)
    if (processedEventsRef.current.size > 100) {
      const tenMinutesAgo = Math.floor(Date.now() / (5 * 60 * 1000)) - 2;
      for (const key of processedEventsRef.current) {
        const keyTime = parseInt(key.split('|')[3] || '0');
        if (keyTime < tenMinutesAgo) {
          processedEventsRef.current.delete(key);
        }
      }
    }

    sendToTerminal(`🔴 ${LOG_PREFIX} Item recusado recebido`, {
      pedidoId: event.pedidoId,
      itemId: event.itemId,
      motivo: event.motivo,
    });

    // Tocar som urgente
    playNotificationSound('urgente');

    // Mostrar notificação modal sticky
    notify({
      type: 'error',
      title: '🔴 Item Recusado',
      message: `Pedido #${event.pedidoId} foi recusado: ${event.motivo}${event.observacao ? `\n\n${event.observacao}` : ''}`,
      sticky: true,
      deduplicationKey: dedupKey,
      actions: [
        {
          label: 'Ver Detalhes',
          onClick: () => {
            navigateToPedido(event.pedidoId, event.itemId);
          },
        },
      ],
    });

    // Atualizar estado: resetar flags e atualizar status
    try {
      const pedido = pedidos.find(p => p.orc_id === event.pedidoId);
      if (pedido) {
        const itemIndex = pedido.itens?.findIndex((it: any) => it.oit_id === event.itemId) ?? -1;
        if (itemIndex >= 0 && pedido.itens) {
          // Criar cópia imutável do pedido e item
          const item = pedido.itens[itemIndex];
          const itemAtualizado = {
            ...item,
            arquivo: item.arquivo ? {
              ...item.arquivo,
              sys_save: false,
              sys_export: false,
              sys_export_layout: false,
            } : undefined,
            status: item.status ? {
              ...item.status,
              oit_status_id: 10,
              descricao: 'Recusa',
            } : undefined,
            __etapa_producao__: 10,
          };
          
          const pedidoAtualizado = {
            ...pedido,
            itens: [
              ...pedido.itens.slice(0, itemIndex),
              itemAtualizado,
              ...pedido.itens.slice(itemIndex + 1),
            ],
          };
          
          // ✅ CORREÇÃO: Atualizar na lista completa (não apenas selectedPedido)
          // Isso garante que os filtros vejam o pedido atualizado
          updatePedidoInList(pedidoAtualizado);
          invalidateOrders(); // schedule background re-fetch for eventual consistency
        }
      } else {
        // Pedido não está na lista, fazer refresh
        fetchPedidos(true);
      }
    } catch (error) {
      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao atualizar estado após recusa`, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [getDedupKey, navigateToPedido, pedidos, updatePedidoInList, fetchPedidos, invalidateOrders]);

  // Handler para mudança de status de pagamento
  const handlePaymentStatusChanged = useCallback((event: OrderPaymentStatusChangedEvent) => {
    const dedupKey = getDedupKey('payment', event.pedidoId, undefined, event.alteradoEm);
    
    // Deduplicação
    if (processedEventsRef.current.has(dedupKey)) {
      return;
    }
    processedEventsRef.current.add(dedupKey);

    sendToTerminal(`💳 ${LOG_PREFIX} Status de pagamento alterado`, {
      pedidoId: event.pedidoId,
      statusAnterior: event.statusAnterior.id_pagamento,
      statusNovo: event.statusNovo.id_pagamento,
    });

    // Determinar tipo de notificação baseado no novo status
    const isPago = event.statusNovo.id_pagamento === 2;
    const isRecusado = event.statusNovo.id_pagamento === 3;

    // Tocar som apenas se foi pago
    if (isPago) {
      playNotificationSound('normal');
    }

    // Mostrar notificação toast
    notify({
      type: isPago ? 'success' : isRecusado ? 'error' : 'warning',
      title: '💳 Status de Pagamento Atualizado',
      message: `Pedido #${event.pedidoId}: ${event.statusAnterior.descricao} → ${event.statusNovo.descricao}`,
      sticky: false,
      deduplicationKey: dedupKey,
      actions: [
        {
          label: 'Ver Pedido',
          onClick: () => {
            navigateToPedido(event.pedidoId);
          },
        },
      ],
    });

    // Atualizar estado do pedido
    try {
      const pedido = pedidos.find(p => p.orc_id === event.pedidoId);
      if (pedido && pedido.status) {
        // Criar cópia imutável
        const pedidoAtualizado = {
          ...pedido,
          status: {
            ...pedido.status,
            id_pagamento: event.statusNovo.id_pagamento,
            descricao: event.statusNovo.descricao,
            descricao_pagamento: event.statusNovo.descricao_pagamento,
          },
        };
        // ✅ CORREÇÃO: Atualizar na lista completa (não apenas selectedPedido)
        // Isso garante que os filtros vejam o status de pagamento atualizado
        // e pedidos pagos sem arquivo não sejam ocultados incorretamente
        updatePedidoInList(pedidoAtualizado);
        invalidateOrders();
      } else {
        // Pedido não está na lista, fazer refresh
        fetchPedidos(true);
      }
    } catch (error) {
      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao atualizar status de pagamento`, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [getDedupKey, navigateToPedido, pedidos, updatePedidoInList, fetchPedidos, invalidateOrders]);

  // Handler para item salvo
  const handleItemSaved = useCallback((event: OrderItemSavedEvent) => {
    const dedupKey = getDedupKey('saved', event.pedidoId, event.itemId, event.salvoEm);
    
    // Deduplicação
    if (processedEventsRef.current.has(dedupKey)) {
      return;
    }
    processedEventsRef.current.add(dedupKey);

    sendToTerminal(`💾 ${LOG_PREFIX} Item salvo`, {
      pedidoId: event.pedidoId,
      itemId: event.itemId,
      role: event.salvoPor.role,
    });

    // Tocar som apenas se produção salvou (vendedor já sabe que salvou)
    // ou se vendedor salvou (produção precisa saber)
    if (event.salvoPor.role === 'producao' || event.salvoPor.role === 'vendedor') {
      playNotificationSound('normal');
    }

    // Mostrar notificação toast
    const message = event.salvoPor.role === 'vendedor' 
      ? `Arquivo do pedido #${event.pedidoId} foi salvo e está pronto para produção`
      : `Arquivo do pedido #${event.pedidoId} foi salvo por ${event.salvoPor.userName}`;

    notify({
      type: 'info',
      title: '💾 Arquivo Salvo',
      message,
      sticky: false,
      deduplicationKey: dedupKey,
    });

    // Atualizar estado: atualizar flags do arquivo
    try {
      const pedido = pedidos.find(p => p.orc_id === event.pedidoId);
      if (pedido) {
        const itemIndex = pedido.itens?.findIndex((it: any) => it.oit_id === event.itemId) ?? -1;
        if (itemIndex >= 0 && pedido.itens) {
          const item = pedido.itens[itemIndex];
          // Criar cópia imutável
          const itemAtualizado = {
            ...item,
            arquivo: item.arquivo ? {
              ...item.arquivo,
              sys_save: event.arquivo.sys_save,
              sys_export: event.arquivo.sys_export,
              sys_export_layout: event.arquivo.sys_export_layout,
              caminho: event.arquivo.caminho,
              nome_arquivo: event.arquivo.nome_arquivo,
            } : {
              sys_save: event.arquivo.sys_save,
              sys_export: event.arquivo.sys_export,
              sys_export_layout: event.arquivo.sys_export_layout,
              caminho: event.arquivo.caminho,
              nome_arquivo: event.arquivo.nome_arquivo,
            },
          };
          
          const pedidoAtualizado = {
            ...pedido,
            itens: [
              ...pedido.itens.slice(0, itemIndex),
              itemAtualizado,
              ...pedido.itens.slice(itemIndex + 1),
            ],
          };
          
          // ✅ CORREÇÃO: Atualizar na lista completa
          updatePedidoInList(pedidoAtualizado);
          invalidateOrders();
        }
      } else {
        // Pedido não está na lista, fazer refresh
        fetchPedidos(true);
      }
    } catch (error) {
      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao atualizar estado após salvamento`, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [getDedupKey, pedidos, updatePedidoInList, fetchPedidos, invalidateOrders]);

  // Handler para mudança de estágio
  const handleItemStageChanged = useCallback((event: OrderItemStageChangedEvent) => {
    const dedupKey = getDedupKey('stage', event.pedidoId, event.itemId, event.alteradoEm);
    
    // Deduplicação
    if (processedEventsRef.current.has(dedupKey)) {
      return;
    }
    processedEventsRef.current.add(dedupKey);

    sendToTerminal(`🔄 ${LOG_PREFIX} Estágio alterado`, {
      pedidoId: event.pedidoId,
      itemId: event.itemId,
      stageNovo: event.stageNovo.sector,
      descricao: event.status.descricao,
    });

    // Tocar som normal
    playNotificationSound('normal');

    // Mostrar notificação toast
    notify({
      type: 'info',
      title: '🔄 Estágio Atualizado',
      message: `Pedido #${event.pedidoId} avançou para: ${event.status.descricao}`,
      sticky: false,
      deduplicationKey: dedupKey,
      actions: [
        {
          label: 'Ver Pedido',
          onClick: () => {
            navigateToPedido(event.pedidoId, event.itemId);
          },
        },
      ],
    });

    // Atualizar estado: atualizar estágio do item
    try {
      const pedido = pedidos.find(p => p.orc_id === event.pedidoId);
      if (pedido) {
        const itemIndex = pedido.itens?.findIndex((it: any) => it.oit_id === event.itemId) ?? -1;
        if (itemIndex >= 0 && pedido.itens) {
          const item = pedido.itens[itemIndex];
          // Criar cópia imutável
          const itemAtualizado = {
            ...item,
            status: item.status ? {
              ...item.status,
              oit_status_id: event.status.oit_status_id,
              descricao: event.status.descricao,
            } : {
              oit_status_id: event.status.oit_status_id,
              descricao: event.status.descricao,
            },
            __etapa_producao__: event.status.oit_status_id,
          };
          
          const pedidoAtualizado = {
            ...pedido,
            itens: [
              ...pedido.itens.slice(0, itemIndex),
              itemAtualizado,
              ...pedido.itens.slice(itemIndex + 1),
            ],
          };
          
          // ✅ CORREÇÃO: Atualizar na lista completa
          updatePedidoInList(pedidoAtualizado);
          invalidateOrders();
        }
      } else {
        // Pedido não está na lista, fazer refresh
        fetchPedidos(true);
      }
    } catch (error) {
      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao atualizar estágio`, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [getDedupKey, navigateToPedido, pedidos, updatePedidoInList, fetchPedidos, invalidateOrders]);

  // Handler para item exportado (baixa prioridade)
  const handleItemExported = useCallback((event: OrderItemExportedEvent) => {
    const dedupKey = getDedupKey('exported', event.pedidoId, event.itemId, event.exportadoEm);
    
    // Deduplicação
    if (processedEventsRef.current.has(dedupKey)) {
      return;
    }
    processedEventsRef.current.add(dedupKey);

    sendToTerminal(`📤 ${LOG_PREFIX} Item exportado`, {
      pedidoId: event.pedidoId,
      itemId: event.itemId,
      formatos: event.arquivo.formatos,
    });

    // Notificação opcional (sem som)
    notify({
      type: 'info',
      title: '📤 Arquivo Exportado',
      message: `Pedido #${event.pedidoId} foi exportado em ${event.arquivo.formatos.join(', ')}`,
      sticky: false,
      deduplicationKey: dedupKey,
    });

    // Atualizar estado: atualizar flags de exportação
    try {
      const pedido = pedidos.find(p => p.orc_id === event.pedidoId);
      if (pedido) {
        const itemIndex = pedido.itens?.findIndex((it: any) => it.oit_id === event.itemId) ?? -1;
        if (itemIndex >= 0 && pedido.itens) {
          const item = pedido.itens[itemIndex];
          // Criar cópia imutável
          const itemAtualizado = {
            ...item,
            arquivo: item.arquivo ? {
              ...item.arquivo,
              sys_export: event.arquivo.sys_export,
            } : {
              sys_export: event.arquivo.sys_export,
            },
          };
          
          const pedidoAtualizado = {
            ...pedido,
            itens: [
              ...pedido.itens.slice(0, itemIndex),
              itemAtualizado,
              ...pedido.itens.slice(itemIndex + 1),
            ],
          };
          
          // ✅ CORREÇÃO: Atualizar na lista completa
          updatePedidoInList(pedidoAtualizado);
          invalidateOrders();
        }
      } else {
        fetchPedidos(true);
      }
    } catch (error) {
      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao atualizar estado após exportação`, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [getDedupKey, pedidos, updatePedidoInList, fetchPedidos, invalidateOrders]);

  // Handler para layout exportado (baixa prioridade)
  const handleItemLayoutExported = useCallback((event: OrderItemLayoutExportedEvent) => {
    const dedupKey = getDedupKey('layout_exported', event.pedidoId, event.itemId, event.exportadoEm);
    
    // Deduplicação
    if (processedEventsRef.current.has(dedupKey)) {
      return;
    }
    processedEventsRef.current.add(dedupKey);

    sendToTerminal(`🖼️ ${LOG_PREFIX} Layout exportado`, {
      pedidoId: event.pedidoId,
      itemId: event.itemId,
    });

    // Notificação opcional (sem som)
    notify({
      type: 'info',
      title: '🖼️ Layout Exportado',
      message: `Layout do pedido #${event.pedidoId} foi exportado`,
      sticky: false,
      deduplicationKey: dedupKey,
    });

    // Atualizar estado: atualizar flag de layout exportado
    try {
      const pedido = pedidos.find(p => p.orc_id === event.pedidoId);
      if (pedido) {
        const itemIndex = pedido.itens?.findIndex((it: any) => it.oit_id === event.itemId) ?? -1;
        if (itemIndex >= 0 && pedido.itens) {
          const item = pedido.itens[itemIndex];
          // Criar cópia imutável
          const itemAtualizado = {
            ...item,
            arquivo: item.arquivo ? {
              ...item.arquivo,
              sys_export_layout: event.arquivo.sys_export_layout,
            } : {
              sys_export_layout: event.arquivo.sys_export_layout,
            },
          };
          
          const pedidoAtualizado = {
            ...pedido,
            itens: [
              ...pedido.itens.slice(0, itemIndex),
              itemAtualizado,
              ...pedido.itens.slice(itemIndex + 1),
            ],
          };
          
          // ✅ CORREÇÃO: Atualizar na lista completa
          updatePedidoInList(pedidoAtualizado);
          invalidateOrders();
        }
      } else {
        fetchPedidos(true);
      }
    } catch (error) {
      sendToTerminal(`❌ ${LOG_PREFIX} Erro ao atualizar estado após exportação de layout`, {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [getDedupKey, pedidos, updatePedidoInList, fetchPedidos, invalidateOrders]);

  // Handler para item aberto (baixa prioridade)
  const handleItemOpened = useCallback((event: OrderItemOpenedEvent) => {
    const dedupKey = getDedupKey('opened', event.pedidoId, event.itemId, event.abertoEm);
    
    // Deduplicação
    if (processedEventsRef.current.has(dedupKey)) {
      return;
    }
    processedEventsRef.current.add(dedupKey);

    sendToTerminal(`📂 ${LOG_PREFIX} Item aberto`, {
      pedidoId: event.pedidoId,
      itemId: event.itemId,
      userName: event.abertoPor.userName,
    });

    // Notificação opcional (sem som) - apenas para informação
    // Pode ser desabilitada se não for necessária
    // notify({
    //   type: 'info',
    //   title: '📂 Arquivo Aberto',
    //   message: `${event.abertoPor.userName} está trabalhando no pedido #${event.pedidoId}`,
    //   sticky: false,
    //   deduplicationKey: dedupKey,
    // });
  }, [getDedupKey]);

  // Usar hook WebSocket
  useOrdersWebSocket(userInfo?.id, {
    onItemRefused: handleItemRefused,
    onPaymentStatusChanged: handlePaymentStatusChanged,
    onItemSaved: handleItemSaved,
    onItemStageChanged: handleItemStageChanged,
    onItemExported: handleItemExported,
    onItemLayoutExported: handleItemLayoutExported,
    onItemOpened: handleItemOpened,
  });

  // Provider não renderiza nada (apenas gerencia eventos)
  return null;
}

